import React, { useMemo } from "react";
import { BECK_THRESHOLD_LINES } from "../utils/patientBeckAffectiveSpectrum";

const CHART = {
  width: 520,
  height: 360,
  padding: { top: 28, right: 24, bottom: 72, left: 52 },
  yMax: 63,
};

const formatDay = (day) => {
  if (!day) return "—";
  const date = new Date(day);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export default function BeckAffectiveSpectrumChart({ spectrum }) {
  const innerWidth = CHART.width - CHART.padding.left - CHART.padding.right;
  const innerHeight = CHART.height - CHART.padding.top - CHART.padding.bottom;
  const baseY = CHART.padding.top + innerHeight;
  const baseX = CHART.padding.left;

  const bars = useMemo(
    () => [
      {
        id: "anxiety",
        label: "Beck Ansiedad",
        score: spectrum?.anxiety?.score,
        severity: spectrum?.anxiety?.severity?.label,
        testDay: spectrum?.anxiety?.testDay,
        color: "#2563eb",
      },
      {
        id: "depression",
        label: "Beck Depresión",
        score: spectrum?.depression?.score,
        severity: spectrum?.depression?.severity?.label,
        testDay: spectrum?.depression?.testDay,
        color: "#7c3aed",
      },
    ],
    [spectrum]
  );

  const scaleY = (value) =>
    CHART.padding.top +
    innerHeight -
    (value / CHART.yMax) * innerHeight;

  const groupWidth = innerWidth / bars.length;
  const barWidth = Math.min(72, groupWidth * 0.45);

  if (!spectrum?.hasData) {
    return (
      <p className="expedientes-dashboard-empty">
        Se requieren puntajes de Beck Ansiedad y/o Beck Depresión para mostrar
        el espectro afectivo.
      </p>
    );
  }

  const yTicks = [0, 14, 20, 22, 29, 36, 63];

  return (
    <div className="expedientes-beck-wrap">
      {spectrum.profile && (
        <div className={`expedientes-beck-profile expedientes-beck-profile--${spectrum.profile.type}`}>
          <strong>{spectrum.profile.label}</strong>
          <span>{spectrum.profile.description}</span>
        </div>
      )}

      <svg
        viewBox={`0 0 ${CHART.width} ${CHART.height}`}
        className="expedientes-beck-chart"
        role="img"
        aria-label="Comparativa Beck Ansiedad versus Beck Depresión"
      >
        {yTicks.map((tick) => {
          const y = scaleY(tick);
          return (
            <g key={`tick-${tick}`}>
              <line
                x1={baseX}
                y1={y}
                x2={baseX + innerWidth}
                y2={y}
                className="expedientes-beck-grid"
              />
              <text
                x={baseX - 10}
                y={y + 4}
                className="expedientes-beck-tick"
                textAnchor="end"
              >
                {tick}
              </text>
            </g>
          );
        })}

        {BECK_THRESHOLD_LINES.map((line) => {
          const y = scaleY(line.value);
          return (
            <line
              key={`${line.test}-${line.value}`}
              x1={baseX}
              y1={y}
              x2={baseX + innerWidth}
              y2={y}
              className={`expedientes-beck-threshold expedientes-beck-threshold--${line.test === "Ansiedad" ? "anxiety" : "depression"}`}
              strokeDasharray="5 4"
            />
          );
        })}

        <line
          x1={baseX}
          y1={baseY}
          x2={baseX + innerWidth}
          y2={baseY}
          className="expedientes-beck-axis"
        />
        <line
          x1={baseX}
          y1={CHART.padding.top}
          x2={baseX}
          y2={baseY}
          className="expedientes-beck-axis"
        />

        {bars.map((bar, index) => {
          const centerX = baseX + groupWidth * index + groupWidth / 2;
          const score = bar.score ?? 0;
          const barHeight = baseY - scaleY(score);
          const hasScore = bar.score != null;

          return (
            <g key={bar.id}>
              {hasScore && (
                <>
                  <rect
                    x={centerX - barWidth / 2}
                    y={scaleY(score)}
                    width={barWidth}
                    height={barHeight}
                    rx="8"
                    className="expedientes-beck-bar"
                    fill={bar.color}
                  />
                  <text
                    x={centerX}
                    y={scaleY(score) - 8}
                    className="expedientes-beck-bar-value"
                    textAnchor="middle"
                  >
                    {bar.score}
                  </text>
                </>
              )}
              <text
                x={centerX}
                y={baseY + 18}
                className="expedientes-beck-bar-label"
                textAnchor="middle"
              >
                {bar.label}
              </text>
              <text
                x={centerX}
                y={baseY + 34}
                className="expedientes-beck-bar-meta"
                textAnchor="middle"
              >
                {hasScore ? bar.severity : "Sin datos"}
              </text>
              <text
                x={centerX}
                y={baseY + 48}
                className="expedientes-beck-bar-meta"
                textAnchor="middle"
              >
                {formatDay(bar.testDay)}
              </text>
            </g>
          );
        })}

        <text
          x={18}
          y={CHART.padding.top + innerHeight / 2}
          className="expedientes-beck-axis-label"
          textAnchor="middle"
          transform={`rotate(-90 18 ${CHART.padding.top + innerHeight / 2})`}
        >
          Puntaje directo
        </text>
      </svg>

      <ul className="expedientes-beck-threshold-legend">
        {BECK_THRESHOLD_LINES.map((line) => (
          <li
            key={`${line.test}-${line.value}`}
            className={`expedientes-beck-threshold-legend-item expedientes-beck-threshold-legend-item--${line.test === "Ansiedad" ? "anxiety" : "depression"}`}
          >
            {line.label} · {line.test} (≥{line.value})
          </li>
        ))}
      </ul>
    </div>
  );
}
